/**
 * BuildSystem.ts — Stage 8: The Venue Circuit
 *
 * Manages four claimable venue nodes on the map. A player unit must move
 * to the venue's access tile and the player must have resources to spend.
 * Once spending is committed the unit claims the venue over VENUE_CLAIM_TIME
 * seconds.  Claimed venues yield per-second income via ResourceSystem and
 * become targets for enemy units.
 *
 * State machine per venue:
 *   unclaimed → (spend + unit arrives) → claiming → (time) → claimed
 *   claimed   → (enemy present, hp → 0)            → unclaimed  (no refund)
 *   claiming  → (claimer unit dies / leaves range)  → unclaimed  (no refund)
 */

import type { Unit }           from '../entities/Unit';
import type { ResourceSystem } from './ResourceSystem';
import type { BalanceValues, VenueLayout, VenueState } from '../types';
import {
  VENUE_CLAIM_COST_REP,
  VENUE_CLAIM_COST_SALES,
  VENUE_CLAIM_TIME,
  VENUE_CLAIM_RANGE,
  VENUE_HP_MAX,
  VENUE_ENEMY_DPS,
  VENUE_REGEN_RATE,
  GEAR_FAN_CLAIM_BOOST,
  GEAR_FAN_CLAIM_BOOST_MAX,
  VENUE_BUILD_GEAR_COST,
  VENUE_BUILD_GEAR_RANGE,
  VENUE_BUILD_GEAR_RATE,
  VENUE_UPGRADE_L2_REP,
  VENUE_UPGRADE_L2_SALES,
  VENUE_UPGRADE_L3_REP,
  VENUE_UPGRADE_L3_SALES,
  VENUE_INCOME_MULT_L2,
  VENUE_INCOME_MULT_L3,
  VENUE_HP_MULT_L2,
  VENUE_HP_MULT_L3,
  VENUE_ENGAGEMENT_DECAY_RATE,
  VENUE_ENGAGEMENT_RECHARGE_RATE,
  VENUE_ENGAGEMENT_WARNING,
} from '../constants';
import { Faction } from '../types';

// ── Seeded PRNG (same algorithm as TileMap) ───────────────────────────────────
function makeRng(seed: number) {
  let s = (seed >>> 0) || 1;
  const next = (): number => {
    s ^= s << 13; s ^= s >>> 17; s ^= s << 5; s = s >>> 0;
    return s / 4294967296;
  };
  return { next, int: (lo: number, hi: number) => lo + Math.floor(next() * (hi - lo + 1)) };
}

export interface VenueNode {
  id:           string;
  name:         string;
  venueType:    VenueType;
  /** Access tile — unit must stand here to claim. */
  col:          number;
  row:          number;
  /** Visual building tile (not walkable). */
  buildingCol:  number;
  buildingRow:  number;
  state:        VenueState;
  progress:     number; // 0–1 while claiming
  hp:           number;
  maxHp:        number;
  claimerUnit:  Unit | null;
  // Per-second income bonuses once claimed (scale with venueLevel)
  repBonus:     number;
  salesBonus:   number;
  fanBonus:     number;
  gearBonus:    number;
  // Base income (Level 1 values — used to compute scaled bonuses on upgrade)
  baseRepBonus:   number;
  baseSalesBonus: number;
  baseFanBonus:   number;
  baseGearBonus:  number;
  // Upgrade tier (1 = base, 2 = improved, 3 = max)
  venueLevel:     1 | 2 | 3;
  // Claim cost
  claimCostRep:   number;
  claimCostSales: number;
  // Runtime attack state (set each tick — do not persist)
  isUnderAttack:  boolean;
  // 0–1: how "alive" the venue feels. Decays when no player unit is nearby;
  // a unit standing at the venue recharges it. All income is multiplied by this.
  engagement:     number;
}

// ── Fan Workshop build-site interface ────────────────────────────────────────

/**
 * A pre-placed construction site that fans can build by depositing GEAR.
 * Once `gearProgress` reaches `gearRequired` the site is promoted to a new
 * unclaimed VenueNode (free to claim — no REP or SALES cost).
 */
export interface FanBuildSite {
  id:           string;
  name:         string;
  /** Access tile — fan must stand here to deposit GEAR. */
  col:          number;
  row:          number;
  /** Tile for the building sprite (one step away, decorative only). */
  buildingCol:  number;
  buildingRow:  number;
  venueType:    VenueType;
  gearRequired: number;
  gearProgress: number;  // GEAR deposited so far (0 → gearRequired)
  fanCount:     number;  // fans actively depositing this frame
  active:       boolean; // true while at least one fan is present
  completed:    boolean; // true once promoted to a VenueNode
}

// ── Venue template pool — randomly assigned to build sites each new game ─────
// type → income (rep, sales, fan, gear) when claimed:
//   pub:      rep+2 sales+1
//   club:     rep+1 sales+3
//   studio:   rep+3 gear+2
//   stage:    fan+3 sales+1
//   hall:     fan+2 sales+2
//   workshop: fan+1 gear+3
export type VenueType = 'pub' | 'club' | 'studio' | 'stage' | 'hall' | 'workshop';

interface VenueTemplate { name: string; type: VenueType; gearCost: number; }

const VENUE_TEMPLATE_POOL: VenueTemplate[] = [
  { name: 'The Dusty Needle',   type: 'pub',      gearCost: 20 },
  { name: 'The Feedback Loop',  type: 'pub',      gearCost: 25 },
  { name: 'The Broken String',  type: 'pub',      gearCost: 20 },
  { name: 'The Amp & Anvil',    type: 'pub',      gearCost: 25 },
  { name: 'The Melody Inn',     type: 'pub',      gearCost: 20 },
  { name: 'Basement Club',      type: 'club',     gearCost: 35 },
  { name: 'The Dark Horse',     type: 'club',     gearCost: 30 },
  { name: 'The Silver Tongue',  type: 'club',     gearCost: 35 },
  { name: 'Backroom Studios',   type: 'studio',   gearCost: 40 },
  { name: 'The Tape Room',      type: 'studio',   gearCost: 30 },
  { name: 'The Analogue Den',   type: 'studio',   gearCost: 45 },
  { name: 'The Neon Stage',     type: 'stage',    gearCost: 45 },
  { name: 'The Market Stage',   type: 'stage',    gearCost: 25 },
  { name: 'The Bandstand',      type: 'stage',    gearCost: 20 },
  { name: 'The Corner Stage',   type: 'stage',    gearCost: 25 },
  { name: 'The Old Granary',    type: 'hall',     gearCost: 50 },
  { name: 'The Guildhall',      type: 'hall',     gearCost: 55 },
  { name: 'The Vinyl Workshop', type: 'workshop', gearCost: 30 },
  { name: 'The Rehearsal Room', type: 'workshop', gearCost: 20 },
  { name: 'The Practice Space', type: 'workshop', gearCost: 25 },
];

function bonusesForType(t: VenueType): { rep: number; sales: number; fan: number; gear: number } {
  switch (t) {
    case 'pub':      return { rep: 2, sales: 1, fan: 0, gear: 0 };
    case 'club':     return { rep: 1, sales: 3, fan: 0, gear: 0 };
    case 'studio':   return { rep: 3, sales: 0, fan: 0, gear: 2 };
    case 'stage':    return { rep: 0, sales: 1, fan: 3, gear: 0 };
    case 'hall':     return { rep: 1, sales: 2, fan: 2, gear: 0 };
    case 'workshop': return { rep: 0, sales: 0, fan: 1, gear: 3 };
  }
}

// ── Build site positions — cx/cy-relative so they scale across all map presets ──
// Each site is near its corresponding Gear Depot so the mine→deposit flow is
// spatially intuitive. Offsets are preserved from the original 80×60 design.
// IDs are positional ('build_0'..'build_4') for stable save/load matching.
function getBuildSitePositions(cx: number, cy: number): Array<{
  id: string; col: number; row: number; buildingCol: number; buildingRow: number;
}> {
  return [
    { id: 'build_0', col: cx - 18, row: cy +  8, buildingCol: cx - 20, buildingRow: cy +  6 }, // near depot_west
    { id: 'build_1', col: cx +  6, row: cy - 12, buildingCol: cx +  6, buildingRow: cy - 14 }, // near depot_north
    { id: 'build_2', col: cx + Math.round(cx * 0.417), row: cy - Math.round(cy * 0.056), buildingCol: cx + Math.round(cx * 0.417), buildingRow: cy - Math.round(cy * 0.083) }, // near edge depot_east
    { id: 'build_3', col: cx - Math.round(cx * 0.396), row: cy - Math.round(cy * 0.306), buildingCol: cx - Math.round(cx * 0.396), buildingRow: cy - Math.round(cy * 0.333) }, // near edge depot_northwest
    { id: 'build_4', col: cx +  2, row: cy + 18, buildingCol: cx +  2, buildingRow: cy + 16 }, // near depot_south
  ];
}

// ── Venue definitions ─────────────────────────────────────────────────────────
// Access tiles verified passable via TileMap generate() analysis.
// layouts[0..3] = inner NW/NE/SW/SE; layouts[4..7] = outer N/E/S/W.
function makeVenueDefs(b: BalanceValues, layouts: VenueLayout[]): Omit<VenueNode,
  'state' | 'progress' | 'hp' | 'maxHp' | 'claimerUnit' | 'isUnderAttack' |
  'venueLevel' | 'baseRepBonus' | 'baseSalesBonus' | 'baseFanBonus' | 'baseGearBonus' | 'engagement'
>[] {
  const L = layouts;
  return [
    {
      id: 'dirty_duck',
      name: 'Verdis',
      venueType: 'pub',
      col: L[0].accessCol, row: L[0].accessRow,
      buildingCol: L[0].buildingCol, buildingRow: L[0].buildingRow,
      repBonus:   b.venueRepDirtyDuck,
      salesBonus: b.venueSalesDirtyDuck,
      fanBonus:   0,
      gearBonus:  0,
      claimCostRep:   VENUE_CLAIM_COST_REP,
      claimCostSales: VENUE_CLAIM_COST_SALES,
    },
    {
      id: 'record_shop',
      name: 'Acorn Records',
      venueType: 'club',
      col: L[1].accessCol, row: L[1].accessRow,
      buildingCol: L[1].buildingCol, buildingRow: L[1].buildingRow,
      repBonus:   0,
      salesBonus: b.venueSalesRecordShop,
      fanBonus:   0,
      gearBonus:  0,
      claimCostRep:   VENUE_CLAIM_COST_REP,
      claimCostSales: VENUE_CLAIM_COST_SALES,
    },
    {
      id: 'community_hall',
      name: 'The Social Club',
      venueType: 'hall',
      col: L[2].accessCol, row: L[2].accessRow,
      buildingCol: L[2].buildingCol, buildingRow: L[2].buildingRow,
      repBonus:   0,
      salesBonus: 0,
      fanBonus:   b.venueFanHall,
      gearBonus:  0,
      claimCostRep:   VENUE_CLAIM_COST_REP,
      claimCostSales: VENUE_CLAIM_COST_SALES,
    },
    {
      id: 'indie_venue',
      name: 'The Quicksilver',
      venueType: 'pub',
      col: L[3].accessCol, row: L[3].accessRow,
      buildingCol: L[3].buildingCol, buildingRow: L[3].buildingRow,
      repBonus:   b.venueRepIndie,
      salesBonus: b.venueSalesIndie,
      fanBonus:   0,
      gearBonus:  0,
      claimCostRep:   VENUE_CLAIM_COST_REP,
      claimCostSales: VENUE_CLAIM_COST_SALES,
    },
    // ── Outer venues (Phase 2) ────────────────────────────────────────────────
    {
      id: 'rooftop_stage',
      name: 'The Lodge',
      venueType: 'stage',
      col: L[4].accessCol, row: L[4].accessRow,
      buildingCol: L[4].buildingCol, buildingRow: L[4].buildingRow,
      repBonus:   b.venueRepRooftopStage,
      salesBonus: 0,
      fanBonus:   b.venueFanRooftopStage,
      gearBonus:  0,
      claimCostRep:   VENUE_CLAIM_COST_REP,
      claimCostSales: VENUE_CLAIM_COST_SALES,
    },
    {
      id: 'arts_centre',
      name: 'The Water Rats',
      venueType: 'hall',
      col: L[5].accessCol, row: L[5].accessRow,
      buildingCol: L[5].buildingCol, buildingRow: L[5].buildingRow,
      repBonus:   0,
      salesBonus: b.venueSalesArtsCentre,
      fanBonus:   b.venueFanArtsCentre,
      gearBonus:  0,
      claimCostRep:   VENUE_CLAIM_COST_REP,
      claimCostSales: VENUE_CLAIM_COST_SALES,
    },
    {
      id: 'underground',
      name: 'The Snooker Club',
      venueType: 'club',
      col: L[6].accessCol, row: L[6].accessRow,
      buildingCol: L[6].buildingCol, buildingRow: L[6].buildingRow,
      repBonus:   b.venueRepUnderground,
      salesBonus: b.venueSalesUnderground,
      fanBonus:   0,
      gearBonus:  0,
      claimCostRep:   VENUE_CLAIM_COST_REP,
      claimCostSales: VENUE_CLAIM_COST_SALES,
    },
    {
      id: 'town_hall',
      name: 'Sin City',
      venueType: 'hall',
      col: L[7].accessCol, row: L[7].accessRow,
      buildingCol: L[7].buildingCol, buildingRow: L[7].buildingRow,
      repBonus:   b.venueRepTownHall,
      salesBonus: 0,
      fanBonus:   b.venueFanTownHall,
      gearBonus:  0,
      claimCostRep:   VENUE_CLAIM_COST_REP,
      claimCostSales: VENUE_CLAIM_COST_SALES,
    },
  ];
}

export class BuildSystem {
  readonly venues: VenueNode[];

  /**
   * Filled by tickClaiming() whenever a venue completes.
   * Game.ts drains this each update frame and spawns Fan units at the tile.
   */
  readonly pendingFanSpawns: Array<{ col: number; row: number }> = [];

  /**
   * Workshop sites that fans can construct by depositing GEAR.
   * Once completed each site promotes itself into a new VenueNode.
   */
  readonly buildSites: FanBuildSite[];

  /**
   * Names of workshop sites that just finished construction this frame.
   * Game.ts drains this to display toast notifications.
   */
  readonly pendingBuildCompletions: string[] = [];

  /**
   * Fires once when a claimed venue first comes under enemy attack (edge trigger).
   * Game.ts drains this to show a warning toast.
   */
  readonly pendingVenueAttacks: string[] = [];

  /**
   * Fires when a claimed venue is destroyed (HP → 0) by enemies.
   * Game.ts drains this to show a danger toast.
   */
  readonly pendingVenueDestroyed: string[] = [];

  /** Tracks whether a venue was under attack last tick (for edge detection). */
  private readonly venueWasUnderAttack = new Map<string, boolean>();

  /**
   * Fires once when a venue's engagement drops below the warning threshold.
   * Game.ts drains this to prompt the player to tour.
   */
  readonly pendingEngagementWarnings: string[] = [];

  /** Venues that have already fired their low-engagement warning (cleared when they recover). */
  private readonly engagementWarned = new Set<string>();

  /** Accumulates delta time for the 1-second venue income tick. */
  private incomeAccum = 0;

  /**
   * Fired once when a player unit enters venue range but the player can't
   * afford to claim it. Game.ts drains this to show a toast.
   * { name, repCost, salesCost, reason }
   */
  readonly pendingAffordabilityHints: Array<{
    name: string; repCost: number; salesCost: number; reason?: string;
  }> = [];

  /**
   * Tracks whether a player unit was near each venue last tick.
   * Used to fire the affordability hint only on the transition (not every frame).
   */
  private readonly venueNearby = new Map<string, boolean>();

  constructor(
    private readonly resources:     ResourceSystem,
    private readonly balance:       BalanceValues,
    private readonly venueLayouts:  VenueLayout[],
    seed = 1,
    mapWidth  = 128,
    mapHeight = 96,
  ) {
    this.venues = makeVenueDefs(this.balance, this.venueLayouts).map(def => ({
      ...def,
      state:          'unclaimed' as VenueState,
      progress:       0,
      hp:             VENUE_HP_MAX,
      maxHp:          VENUE_HP_MAX,
      claimerUnit:    null,
      isUnderAttack:  false,
      venueLevel:     1 as const,
      baseRepBonus:   def.repBonus,
      baseSalesBonus: def.salesBonus,
      baseFanBonus:   def.fanBonus,
      baseGearBonus:  def.gearBonus,
      engagement:     1.0,
    }));

    // Seeded Fisher-Yates shuffle → pick one template per build site
    const rng = makeRng(seed ^ 0xBEEF);
    const pool = [...VENUE_TEMPLATE_POOL];
    for (let i = pool.length - 1; i > 0; i--) {
      const j = rng.int(0, i);
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }

    const cx = Math.floor(mapWidth  / 2);
    const cy = Math.floor(mapHeight / 2);
    this.buildSites = getBuildSitePositions(cx, cy).map((def, i) => {
      const template = pool[i % pool.length];
      return {
        ...def,
        name:         template.name,
        venueType:    template.type,
        gearRequired: template.gearCost,
        gearProgress: 0,
        fanCount:     0,
        active:       false,
        completed:    false,
      };
    });
  }

  update(units: Unit[], delta: number): void {
    const alive = units.filter(u => u.alive);

    for (const venue of this.venues) {
      switch (venue.state) {
        case 'unclaimed': this.tickUnclaimed(venue, alive); break;
        case 'claiming':  this.tickClaiming(venue, alive, delta); break;
        case 'claimed':   this.tickClaimed(venue, alive, delta); break;
      }
    }

    // Award venue income every second, scaled by each venue's engagement.
    // This replaces the old addVenueBonus() registration model so engagement
    // can modulate income per-venue without ResourceSystem knowing about it.
    this.incomeAccum += delta;
    if (this.incomeAccum >= 1.0) {
      this.incomeAccum -= 1.0;
      for (const venue of this.venues) {
        if (venue.state !== 'claimed') continue;
        const e = venue.engagement;
        if (venue.repBonus   > 0) this.resources.addVenueReputation(venue.repBonus * e, venue.venueType === 'pub');
        if (venue.salesBonus > 0) this.resources.add('sales',      venue.salesBonus * e);
        if (venue.fanBonus   > 0) this.resources.add('fanbase',    venue.fanBonus   * e);
        if (venue.gearBonus  > 0) this.resources.add('gear',       venue.gearBonus  * e);
      }
    }

    this.tickBuildSites(alive, delta);
  }

  // ── Fan Workshop ──────────────────────────────────────────────────────────

  private tickBuildSites(units: Unit[], delta: number): void {
    for (const site of this.buildSites) {
      if (site.completed) continue;

      // Count fans standing within deposit range
      site.fanCount = 0;
      for (const unit of units) {
        if (unit.bandClass !== 'fan' || unit.faction !== Faction.Player) continue;
        const d = Math.hypot(unit.gridPos.col - site.col, unit.gridPos.row - site.row);
        if (d <= VENUE_BUILD_GEAR_RANGE) site.fanCount++;
      }
      site.active = site.fanCount > 0;

      // Deposit GEAR from the shared pool into the site
      if (site.active && this.resources.gear > 0 && this.resources.progression.canBuildExpansion) {
        const needed  = site.gearRequired - site.gearProgress;
        const maxDeposit = site.fanCount * VENUE_BUILD_GEAR_RATE * delta;
        const deposit = Math.min(maxDeposit, this.resources.gear, needed);
        if (deposit > 0 && this.resources.spend({ gear: deposit })) {
          site.gearProgress += deposit;
        }
      }

      // Complete → promote to an unclaimed venue (free to claim)
      if (site.gearProgress >= site.gearRequired) {
        site.gearProgress = site.gearRequired; // clamp
        site.completed    = true;

        const bonuses = bonusesForType(site.venueType);
        this.venues.push({
          id:             site.id,
          name:           site.name,
          venueType:      site.venueType,
          col:            site.col,
          row:            site.row,
          buildingCol:    site.buildingCol,
          buildingRow:    site.buildingRow,
          state:          'unclaimed',
          progress:       0,
          hp:             VENUE_HP_MAX,
          maxHp:          VENUE_HP_MAX,
          claimerUnit:    null,
          repBonus:       bonuses.rep,
          salesBonus:     bonuses.sales,
          fanBonus:       bonuses.fan,
          gearBonus:      bonuses.gear,
          baseRepBonus:   bonuses.rep,
          baseSalesBonus: bonuses.sales,
          baseFanBonus:   bonuses.fan,
          baseGearBonus:  bonuses.gear,
          venueLevel:     1,
          claimCostRep:   0,
          claimCostSales: 0,
          isUnderAttack:  false,
          engagement:     1.0,
        });

        this.pendingBuildCompletions.push(site.name);
      }
    }
  }

  /**
   * Upgrade a claimed venue to the next level (1→2 or 2→3).
   * Deducts the upgrade cost, scales income bonuses and HP, bumps venueLevel.
   * Returns whether the upgrade succeeded and a reason string on failure.
   */
  upgradeVenue(id: string): { success: boolean; reason?: string } {
    const venue = this.venues.find(v => v.id === id);
    if (!venue || venue.state !== 'claimed') return { success: false, reason: 'not claimed' };
    if (venue.venueLevel >= 3)               return { success: false, reason: 'max level' };

    const nextLevel = (venue.venueLevel + 1) as 2 | 3;
    const progress = this.resources.progression;
    if (nextLevel === 2 && !progress.canUpgradeLevel2) return { success: false, reason: progress.nextGoal };
    if (nextLevel === 3 && !progress.canUpgradeLevel3) return { success: false, reason: progress.nextGoal };

    const repCost   = nextLevel === 2 ? VENUE_UPGRADE_L2_REP   : VENUE_UPGRADE_L3_REP;
    const salesCost = nextLevel === 2 ? VENUE_UPGRADE_L2_SALES : VENUE_UPGRADE_L3_SALES;

    if (!this.resources.canAfford({ reputation: repCost, sales: salesCost })) {
      return { success: false, reason: 'cannot afford' };
    }
    this.resources.spend({ reputation: repCost, sales: salesCost });

    // Scale income from base (Level 1) values; the accumulator in update() picks
    // up the new values on the next tick without any manual re-registration.
    const mult       = nextLevel === 2 ? VENUE_INCOME_MULT_L2 : VENUE_INCOME_MULT_L3;
    venue.repBonus   = Math.round(venue.baseRepBonus   * mult);
    venue.salesBonus = Math.round(venue.baseSalesBonus * mult);
    venue.fanBonus   = Math.round(venue.baseFanBonus   * mult);
    venue.gearBonus  = Math.round(venue.baseGearBonus  * mult);

    // Scale HP — preserve current health ratio so an upgrade mid-siege doesn't instant-heal
    const hpMult   = nextLevel === 2 ? VENUE_HP_MULT_L2 : VENUE_HP_MULT_L3;
    const hpRatio  = venue.maxHp > 0 ? venue.hp / venue.maxHp : 1;
    venue.maxHp    = Math.round(VENUE_HP_MAX * hpMult);
    venue.hp       = Math.round(venue.maxHp * hpRatio);

    venue.venueLevel = nextLevel;
    return { success: true };
  }

  // ── State handlers ────────────────────────────────────────────────────────

  private tickUnclaimed(venue: VenueNode, units: Unit[]): void {
    // Find a player unit adjacent to the access tile
    const claimer = this.nearestPlayerUnit(venue, units);
    const wasNearby = this.venueNearby.get(venue.id) ?? false;
    this.venueNearby.set(venue.id, !!claimer);

    if (!claimer) return;

    // Fan units claim for free — they are the venue's own audience doing
    // the legwork.  Heroes and recruits pay the usual REP + SALES upfront.
    const isFan = claimer.bandClass === 'fan';

    if (!isFan) {
      if (!this.resources.canAfford({
        reputation: venue.claimCostRep,
        sales:      venue.claimCostSales,
      })) {
        // Fire a hint toast once, on the moment a unit first arrives near the
        // venue (false → true transition). Avoids spamming every frame.
        if (!wasNearby) {
          this.pendingAffordabilityHints.push({
            name:      venue.name,
            repCost:   venue.claimCostRep,
            salesCost: venue.claimCostSales,
            reason:    'REP books venues; SALES keeps the operation moving.',
          });
        }
        return;
      }

      // Spend upfront (Warcraft style)
      this.resources.spend({
        reputation: venue.claimCostRep,
        sales:      venue.claimCostSales,
      });
    }

    venue.state       = 'claiming';
    venue.progress    = 0;
    venue.claimerUnit = claimer;
  }

  private tickClaiming(venue: VenueNode, units: Unit[], delta: number): void {
    const claimer = venue.claimerUnit;

    // Abort if the claimer died or wandered away (no refund)
    if (!claimer || !claimer.alive || !this.inRange(venue, claimer)) {
      this.resetVenue(venue);
      return;
    }
    // Pause (don't reset) while claimer is downed — reviving them in range resumes the claim.
    if (claimer.isDowned) return;

    // Fans present alongside the claimer speed up the booking process.
    // Each fan within range contributes +GEAR_FAN_CLAIM_BOOST to the speed multiplier
    // (capped at GEAR_FAN_CLAIM_BOOST_MAX fans contributing).
    const fansNearby = units.filter(
      u => u !== claimer && u.bandClass === 'fan' && u.alive && !u.isDowned && this.inRange(venue, u),
    ).length;
    const speedMult  = 1 + Math.min(fansNearby, GEAR_FAN_CLAIM_BOOST_MAX) * GEAR_FAN_CLAIM_BOOST;

    venue.progress += (delta / VENUE_CLAIM_TIME) * speedMult;

    if (venue.progress >= 1.0) {
      venue.progress    = 1.0;
      venue.state       = 'claimed';
      venue.hp          = venue.maxHp;
      venue.claimerUnit = null;
      venue.engagement  = 1.0;
      // Queue fan spawns — Game.ts drains this and places units on the map
      this.pendingFanSpawns.push({ col: venue.col, row: venue.row });
    }
  }

  private tickClaimed(venue: VenueNode, units: Unit[], delta: number): void {
    // Check for enemies on or adjacent to the access tile
    const enemyPresent = units.some(
      u => u.faction === Faction.Enemy
        && Math.hypot(u.gridPos.col - venue.col, u.gridPos.row - venue.row) <= VENUE_CLAIM_RANGE,
    );

    // Edge-trigger: fire a warning toast the first frame enemies arrive
    const wasUnderAttack = this.venueWasUnderAttack.get(venue.id) ?? false;
    venue.isUnderAttack  = enemyPresent;
    this.venueWasUnderAttack.set(venue.id, enemyPresent);
    if (enemyPresent && !wasUnderAttack) {
      this.pendingVenueAttacks.push(venue.name);
    }

    if (enemyPresent) {
      venue.hp -= VENUE_ENEMY_DPS * delta;
      if (venue.hp <= 0) {
        venue.hp = 0;
        this.resources.onVenueLostToEnemy();
        this.venueWasUnderAttack.set(venue.id, false);
        this.pendingVenueDestroyed.push(venue.name);
        this.resetVenue(venue);
        return;
      }
    } else if (venue.hp < venue.maxHp) {
      // Regenerate when safe
      venue.hp = Math.min(
        venue.hp + venue.maxHp * VENUE_REGEN_RATE * delta,
        venue.maxHp,
      );
    }

    // Engagement: recharge when a player unit is near, decay otherwise.
    const playerNearby = units.some(
      u => u.faction === Faction.Player && u.alive && !u.isDowned && this.inRange(venue, u),
    );
    if (playerNearby) {
      venue.engagement = Math.min(1.0, venue.engagement + VENUE_ENGAGEMENT_RECHARGE_RATE * delta);
      this.engagementWarned.delete(venue.id);
    } else {
      venue.engagement = Math.max(0.0, venue.engagement - VENUE_ENGAGEMENT_DECAY_RATE * delta);
      if (venue.engagement < VENUE_ENGAGEMENT_WARNING && !this.engagementWarned.has(venue.id)) {
        this.engagementWarned.add(venue.id);
        this.pendingEngagementWarnings.push(venue.name);
      }
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  private nearestPlayerUnit(venue: VenueNode, units: Unit[]): Unit | null {
    let best: Unit | null = null;
    let bestDist = VENUE_CLAIM_RANGE;
    for (const u of units) {
      if (u.faction !== Faction.Player) continue;
      if (u.isDowned) continue;
      const d = Math.hypot(u.gridPos.col - venue.col, u.gridPos.row - venue.row);
      if (d <= bestDist) { best = u; bestDist = d; }
    }
    return best;
  }

  private inRange(venue: VenueNode, unit: Unit): boolean {
    return Math.hypot(
      unit.gridPos.col - venue.col,
      unit.gridPos.row - venue.row,
    ) <= VENUE_CLAIM_RANGE;
  }

  private resetVenue(venue: VenueNode): void {
    venue.state         = 'unclaimed';
    venue.progress      = 0;
    venue.hp            = venue.maxHp;
    venue.claimerUnit   = null;
    venue.isUnderAttack = false;
  }
}
